// RootResolver — thin wrapper around `lib/resolveLanding`.
//
// The 5-case resolution logic lives in the pure function so it's testable
// in isolation. This component handles only: (a) the loading state while
// useMe() is in flight, (b) the defensive fallback if useMe returns no
// data (ProtectedRoute should have caught it earlier), (c) issuing the
// React Router Navigate to the function's chosen path.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe } from '../hooks/useMe.js';
import { Spinner } from '../components/ui/Spinner.js';
import { resolveLanding } from '../lib/resolveLanding.js';

export function RootResolver() {
  const { data: me, isLoading } = useMe();
  const { t } = useTranslation('common');
  // Capture "now" once (lazy init — the pure place to read the clock) for the
  // trial-reachability check in resolveLanding.
  const [now] = useState(() => Date.now());

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div role="status" aria-live="polite">
          <Spinner size={32} className="text-[var(--color-text-secondary)]" />
          <span className="sr-only">{t('statuses.loading')}</span>
        </div>
      </div>
    );
  }

  if (!me) {
    return <Navigate to="/login" replace />;
  }

  const { path } = resolveLanding(me, now);
  return <Navigate to={path} replace />;
}
